"use client";
import React, { useState, useEffect } from 'react';
import type { LucideIcon } from 'lucide-react';
import {
  Headphones,
  ShoppingCart,
  MessageCircle,
  History,
  MessageSquareWarning,
  ArrowUpToLine,
} from 'lucide-react';
import { useCartStore } from '@/lib/store/cart.store';
import { useCartDrawerOpenStore } from '@/lib/store/cartdraweropen.store';
import { Placeholder } from '@/components/placeholder';
import { useRouter } from 'next/navigation';

type SidebarItem = {
  icon: LucideIcon;
  label: string;
  badge?: number;
  onClick?: () => void;
  /** 滚动超过阈值后才显示（置顶） */
  showWhenScrolled?: boolean;
  /** 顶部分隔线 */
  dividerTop?: boolean;
  /** 悬停时在左侧展示公众号二维码（与 footer 一致） */
  wechatQr?: boolean;
};

export function RightSidebar() {
  const [showBackToTop, setShowBackToTop] = useState(false);
  const router = useRouter();

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 200) {
        setShowBackToTop(true);
      } else {
        setShowBackToTop(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const [total, setTotal] = useState(0);
  const { open, setOpen } = useCartDrawerOpenStore();
  const { cartCount, setCartCount, getCartList } = useCartStore();
  
  useEffect(() => {
      setTotal(cartCount);
      cartCount && getCartList();
  }, [cartCount]);


  const items: SidebarItem[] = [
    { icon: Headphones, label: '客服', onClick: () => router.push('/customer-service') },
    { icon: ShoppingCart, label: '购物车', badge: total, onClick: () => setOpen(true) },
    { icon: MessageCircle, label: '公众号', wechatQr: true },
    { icon: History, label: '浏览记录' , onClick: () => router.push('/footprint')},
    // { icon: MessageSquareWarning, label: '问题反馈' },
    {
      icon: ArrowUpToLine,
      label: '置顶',
      showWhenScrolled: true,
      onClick: scrollToTop,
      dividerTop: true,
    },
  ];

  return (
    <div className="fixed right-0 top-1/2 z-50 flex -translate-y-1/2 flex-col overflow-visible rounded-l-2xl border border-gray-100 border-r-0 bg-white py-3 shadow-[0_4px_24px_rgba(0,0,0,0.08)]">
      {items.map((item, index) => {
        const row = (
          <div
            onClick={item.onClick}
            className="group relative flex h-[72px] w-[72px] cursor-pointer flex-col items-center justify-center overflow-visible text-[#666] transition-colors hover:text-main"
          >
            {item.wechatQr && (
              <div
                className="pointer-events-none absolute right-full top-[11px] z-[60] w-max translate-x-[-8px] rounded-lg border border-gray-100 bg-white p-2 opacity-0 shadow-[0_4px_24px_rgba(0,0,0,0.08)] transition-opacity duration-200 group-hover:pointer-events-auto group-hover:opacity-100"
                role="tooltip"
              >
                <div className="c-flex flex-col">
                  <div className="relative w-[120px]">
                    <Placeholder
                      src="/site/app-xiazai.png"
                      imageWidth={120}
                      imageHeight={120}
                    />
                    <div className="absolute inset-0 c-flex">
                      <Placeholder
                        src="/site/logo.png"
                        imageWidth={30}
                        imageHeight={30}
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}
            {item.dividerTop && (
              <div className="w-8 h-[1px] bg-gray-200 absolute top-0" />
            )}
            <div className="relative flex flex-col items-center">
              <item.icon
                className={`w-7 h-7 mb-1.5 ${item.dividerTop ? 'mt-2' : ''}`}
                strokeWidth={1.5}
              />
              {item.badge !== undefined && (
                <span className="absolute -top-1.5 -right-3 bg-[#ff5f00] text-white text-[12px] font-medium px-1.5 py-0.5 rounded-full min-w-[20px] text-center leading-none border border-white">
                  {item.badge}
                </span>
              )}
            </div>
            <span className="text-xs">{item.label}</span>
          </div>
        );

        if (item.showWhenScrolled) {
          return (
            <div
              key={index}
              className={`overflow-hidden transition-all duration-300 ease-in-out ${
                showBackToTop ? 'h-[72px] opacity-100 mt-2' : 'h-0 opacity-0 mt-0'
              }`}
            >
              {row}
            </div>
          );
        }

        return <React.Fragment key={index}>{row}</React.Fragment>;
      })}
    </div>
  );
}
